import React from 'react';
import { AlertTriangle, CheckCircle, DollarSign } from 'lucide-react';
import type { RiskAssessment as RiskAssessmentType } from '../../../types/acquisitionAnalysis';

interface RiskAssessmentProps {
  data: RiskAssessmentType;
}

export const RiskAssessment: React.FC<RiskAssessmentProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `~${value.toFixed(1)}%`;
  };

  const getAssessmentBadge = (assessment: string) => {
    const configs: Record<
      string,
      { className: string; icon: typeof CheckCircle | typeof AlertTriangle }
    > = {
      Excellent: {
        className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
        icon: CheckCircle,
      },
      Good: {
        className: 'bg-green-500/20 text-green-300 border-green-500/30',
        icon: CheckCircle,
      },
      Moderate: {
        className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
        icon: AlertTriangle,
      },
      High: {
        className: 'bg-red-500/20 text-red-300 border-red-500/30',
        icon: AlertTriangle,
      },
    };

    const config = configs[assessment] || configs.Moderate;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${config.className}`}
      >
        <Icon className="w-4 h-4" />
        {assessment}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          Clawback Exposure
          {data.clawbackExposure.fiscalYear && (
            <span className="text-sm font-normal text-slate-400">
              ({data.clawbackExposure.fiscalYear})
            </span>
          )}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Total Clawbacks {data.clawbackExposure.fiscalYear && `(${data.clawbackExposure.fiscalYear})`}</p>
            <p className="text-2xl font-bold text-red-300">
              {formatCurrency(data.clawbackExposure.totalClawbacks)}
            </p>
          </div>
          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">As % of Upfront</p>
            <p className="text-2xl font-bold text-indigo-300">
              {formatPercent(data.clawbackExposure.percentOfUpfront)}
            </p>
          </div>
          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Assessment</p>
            <div className="mt-1">
              {getAssessmentBadge(data.clawbackExposure.assessment)}
            </div>
          </div>
        </div>

        {data.clawbackExposure.assessment === 'Excellent' && (
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <p className="text-sm text-emerald-300">
              <strong>Excellent Clawback Profile:</strong> Very low clawback rate indicates a
              high-quality loan book with minimal early discharges. This reduces post-acquisition
              risk significantly.
            </p>
          </div>
        )}

        {data.clawbackExposure.assessment === 'High' && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-300">
              <strong>High Clawback Exposure:</strong> Elevated clawback rate may indicate loan
              quality issues or customer retention challenges. This should be factored into
              valuation and post-acquisition integration planning.
            </p>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" />
          Additional Revenue Streams
        </h3>
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm text-slate-400">Non-Mortgage Income</p>
            <p className="text-2xl font-bold text-green-300">
              {formatCurrency(data.additionalRevenue.nonMortgageIncome)}
            </p>
          </div>
          <p className="text-xs text-green-400">
            This was not included in trail valuation but adds additional value to the acquisition.
          </p>
        </div>
      </div>

      <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
        <h4 className="text-sm font-semibold text-indigo-300 mb-2">Risk Summary</h4>
        <ul className="space-y-1 text-sm text-indigo-300">
          <li className="flex items-start gap-2">
            <span className="text-indigo-500 mt-1">•</span>
            <span>
              Clawback exposure is {data.clawbackExposure.assessment.toLowerCase()}, representing{' '}
              {formatPercent(data.clawbackExposure.percentOfUpfront)} of upfront commissions
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-500 mt-1">•</span>
            <span>
              Additional revenue of {formatCurrency(data.additionalRevenue.nonMortgageIncome)}{' '}
              provides diversification beyond trail income
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-500 mt-1">•</span>
            <span>
              Recommend full due diligence on loan book quality and broker retention agreements
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};
